import { Group } from "~/types/group";

const API_BASE_URL = "/api";

export interface GroupsForUserResponse {
  success: boolean;
  ownedGroups?: Group[];
  memberOfGroups?: Group[];
  error?: string;
}

// Interface for the membership response from the API
interface GroupMembership {
  group_id: number;
  role: string;
  joined_at: string;
  group: Group;
}

/**
 * Fetch all groups for a specific user, split into owned groups and groups the user is a member of
 * @param userId Optional user ID (not used as the backend gets the current user from the session)
 * @returns Promise resolving to groups categorized by ownership status
 */
export const fetchGroupsForUser = async (userId?: number): Promise<GroupsForUserResponse> => {
  try {
    // Fetch both owned groups and memberships in parallel
    const [ownedResponse, membershipResponse] = await Promise.all([
      fetch(`${API_BASE_URL}/groups/owned`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include"
      }),
      fetch(`${API_BASE_URL}/groups/memberships`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include"
      })
    ]);

    // Handle errors for owned groups
    if (!ownedResponse.ok) {
      console.error("Failed to fetch owned groups");
      return {
        success: false,
        error: "Failed to fetch owned groups"
      };
    }

    // Handle errors for member groups
    if (!membershipResponse.ok) {
      console.error("Failed to fetch group memberships");
      return {
        success: false,
        error: "Failed to fetch group memberships"
      };
    }

    // Parse both responses
    const ownedGroups = await ownedResponse.json();
    const membershipData = await membershipResponse.json();

    // Extract just the group data from memberships
    const memberOfGroups = membershipData.map((membership: GroupMembership) => membership.group);

    console.log("Data from group responses:", { ownedGroups, memberOfGroups });

    return {
      success: true,
      ownedGroups: ownedGroups || [],
      memberOfGroups: memberOfGroups || []
    };
  } catch (error) {
    console.error("Error fetching groups:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred fetching groups"
    };
  }
};

/**
 * Fetch groups owned by a specific user
 * @param userId Optional user ID (not used as the backend gets the current user from the session)
 * @returns Promise resolving to an array of groups
 */
export const fetchOwnedGroups = async (userId?: number): Promise<Group[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/groups/owned`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      console.error("Failed to fetch owned groups");
      return [];
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching owned groups:", error);
    return [];
  }
};

/**
 * Fetch groups a specific user is a member of
 * @param userId Optional user ID (not used as the backend gets the current user from the session)
 * @returns Promise resolving to an array of groups
 */
export const fetchMemberGroups = async (userId?: number): Promise<Group[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/groups/memberships`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      console.error("Failed to fetch member groups");
      return [];
    }

    const membershipData = await response.json();
    // Extract just the group data from memberships
    return membershipData.map((membership: GroupMembership) => membership.group);
  } catch (error) {
    console.error("Error fetching member groups:", error);
    return [];
  }
};

/**
 * Fetch all members in a specific group
 * @param groupId The ID of the group to get members for
 * @returns Promise resolving to an array of members with their group membership information
 */
export const fetchGroupMembers = async (
  groupId: number
): Promise<
  {
    member: {
      id: number;
      created_by: number;
      user_id: number;
      name: string;
      email: string | null;
      birthdate: string | null;
      gender: string;
      profile_image_url: string | null;
      created_at: string;
      updated_at: string;
    };
    groupMembership: {
      role: string;
      joined_at: string;
      added_by_user_id: number;
    };
  }[]
> => {
  try {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      console.error(`Failed to fetch members for group ${groupId}`);
      return [];
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching members for group ${groupId}:`, error);
    return [];
  }
};

/**
 * Toggle admin status for a member in a group
 * @param groupId The ID of the group
 * @param memberId The ID of the member to toggle admin status for
 * @returns Promise resolving to the result of the operation
 */
export const toggleMemberAdminStatus = async (
  groupId: number,
  memberId: number
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    memberId: number;
    groupId: number;
    newRole: string;
  };
}> => {
  console.log(`API call: toggleMemberAdminStatus - groupId=${groupId}, memberId=${memberId}`);

  try {
    const url = `${API_BASE_URL}/groups/${groupId}/members/${memberId}/toggle-admin`;
    console.log("Making request to:", url);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({}),
      credentials: "include"
    });

    console.log("API response status:", response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => {
        console.error("Failed to parse error response");
        return { error: "Failed to toggle admin status" };
      });
      console.error("Error response data:", errorData);

      return {
        success: false,
        error: errorData.error || "Failed to toggle admin status"
      };
    }

    const result = await response.json();
    console.log("API success response:", result);

    return {
      success: true,
      message: result.message,
      data: result.data
    };
  } catch (error) {
    console.error("Error in toggleMemberAdminStatus API call:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred toggling admin status"
    };
  }
};
